<script lang="ts">
  import { onMount } from "svelte";
  import AuthLayout from "./AuthLayout.svelte";
  import Banner from "$lib/components/Banner.svelte";
  import Button from "$lib/components/Button.svelte";
  import Select from "$lib/components/Select.svelte";
  import { getExtensionAccess, requestExtensionActivation } from "$lib/api/endpoints";
  import type { ExtensionAccessOption, ExtensionActivation } from "$lib/api/contract";
  import { AppError } from "$lib/api/problem";
  import { newIdempotencyKey } from "$lib/api/idempotency";
  import { session } from "$lib/stores/session.svelte";
  import { track } from "$lib/telemetry/emit";
  import { router } from "../../router.svelte";

  let options = $state<ExtensionAccessOption[]>([]);
  let selectedId = $state("");
  let loading = $state(true);
  let requesting = $state(false);
  let switchingAccount = $state(false);
  let error = $state<AppError | null>(null);
  let result = $state<ExtensionActivation | null>(null);
  let requestKey = $state("");

  const selected = $derived(options.find((option) => option.organizationId === selectedId) ?? options[0] ?? null);
  const selectOptions = $derived(options.map((option) => ({ value: option.organizationId, label: option.organizationName })));

  onMount(() => {
    const controller = new AbortController();
    void getExtensionAccess(controller.signal)
      .then((access) => {
        options = access.organizations;
        selectedId = access.organizations.find((option) => option.status === "ready")?.organizationId
          ?? access.organizations[0]?.organizationId
          ?? "";
        track({
          name: "extension.access.viewed",
          organizationCount: access.organizations.length,
          eligibleCount: access.organizations.filter((option) => option.status === "ready").length
        });
      })
      .catch((caught) => {
        if (caught instanceof AppError && !controller.signal.aborted) error = caught;
      })
      .finally(() => {
        if (!controller.signal.aborted) loading = false;
      });
    return () => controller.abort();
  });

  async function requestKeyDelivery(): Promise<void> {
    if (!selected || selected.status !== "ready" || requesting) return;
    requesting = true;
    error = null;
    result = null;
    requestKey ||= newIdempotencyKey();
    try {
      result = await requestExtensionActivation(selected.organizationId, requestKey);
      requestKey = "";
      track({
        name: "extension.activation.requested",
        orgId: selected.organizationId,
        outcome: result.status === "failed" ? "failure" : "success"
      });
    } catch (caught) {
      track({ name: "extension.activation.requested", orgId: selected.organizationId, outcome: "failure" });
      if (caught instanceof AppError) error = caught;
    } finally {
      requesting = false;
    }
  }

  async function useDifferentAccount(): Promise<void> {
    if (switchingAccount || requesting) return;
    switchingAccount = true;
    await session.logout();
    router.navigate("/login?returnTo=%2Fextension-login", { replace: true });
  }

  function statusMessage(option: ExtensionAccessOption | null): string {
    if (!option) return "No EpiDev organization is linked to this verified account.";
    if (option.status === "seat_required") return "Your organization must assign you an active EpiDev seat before you can request an activation key.";
    if (option.status === "service_unavailable") return "Activation delivery is temporarily unavailable. Try again later.";
    return "Request a fresh one-time activation key for this EpiDev organization. The key is sent to your verified email and is never exposed to this browser.";
  }
</script>

<AuthLayout title="Sign in to the EpiDev extension" subtitle={`Verified as ${session.user?.email ?? ""}`}>
  <div class="stack">
    {#if loading}
      <p class="status-copy" role="status">Checking your extension access…</p>
    {:else}
      {#if options.length > 1}
        <Select id="extension-org" label="Organization" options={selectOptions} bind:value={selectedId} />
      {/if}

      {#if error}
        <Banner tone="error" title="We couldn't request extension access" correlationId={error.correlationId}>
          {error.userMessage}
        </Banner>
      {:else if result}
        {#if result.status === "failed"}
          <Banner tone="error" title="Activation email wasn't sent" role="status">
            We couldn't queue the one-time key. Try again shortly or contact support if
            the problem continues.
          </Banner>
        {:else}
          <Banner tone="ok" title="Activation email requested" role="status">
            {#if result.maskedRecipient}
              A fresh one-time key is being delivered to <span class="mono">{result.maskedRecipient}</span>.
            {:else}
              A fresh one-time key is being delivered to your verified email.
            {/if}
          </Banner>
        {/if}
      {/if}

      <p class="status-copy">{statusMessage(selected)}</p>

      {#if selected?.status === "ready"}
        <Button fullWidth loading={requesting} onclick={requestKeyDelivery}>Email a new activation key</Button>
        <ol class="steps">
          <li>Return to VS Code after the email arrives.</li>
          <li>Open the EpiDev Activation view or run <span class="mono">EpiDev: Sign In</span>.</li>
          <li>Paste the complete <span class="mono">epi_…</span> key. It is consumed once and stored securely by VS Code.</li>
        </ol>
      {:else if options.length === 0}
        <Button href="/welcome" fullWidth>Check linked purchases</Button>
      {/if}

      <Button href="/" variant="ghost" fullWidth>Open customer portal</Button>
      <p class="support">
        Need help? Contact <a href="mailto:support@epidev.ai">support@epidev.ai</a> from your verified email address.
      </p>
    {/if}

    <Button
      variant="ghost"
      fullWidth
      disabled={requesting}
      loading={switchingAccount}
      onclick={useDifferentAccount}
    >Use a different account</Button>
  </div>
</AuthLayout>

<style>
  .status-copy {
    margin: 0;
    font-size: 14.5px;
  }
  .steps {
    display: flex;
    flex-direction: column;
    gap: 7px;
    margin: 0;
    padding-left: 20px;
    color: var(--muted);
    font-size: 13.5px;
  }
  .support {
    margin: 0;
    color: var(--muted);
    font-size: 13px;
    text-align: center;
  }
  .support a {
    color: var(--accent);
  }
</style>
